import React from 'react';
import { 
  ExclamationTriangleIcon,
  InformationCircleIcon,
  MagnifyingGlassIcon,
  CpuChipIcon,
  ServerStackIcon,
  ChartBarIcon,
  CheckCircleIcon,
  ArrowPathIcon,
  AdjustmentsHorizontalIcon,
  ChevronDownIcon
} from '@heroicons/react/24/outline';

export default function SystemHealth() {
  return (
    <div className="px-8 py-8 max-w-[1400px] mx-auto flex flex-col gap-6 pb-20">
      
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-2">
        <h1 className="text-[32px] font-extrabold text-slate-900 tracking-tight">System Health Telemetry</h1>
        <div className="flex items-center gap-2 bg-brand-50 px-4 py-2 rounded-full border border-brand-100">
          <div className="w-2 h-2 bg-brand-500 rounded-full animate-pulse"></div>
          <span className="text-[11px] font-extrabold text-brand-600 uppercase tracking-widest">Live Update</span>
        </div>
      </div>

      {/* Alerts Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        
        {/* Alert 1 */}
        <div className="bg-red-50/50 border border-red-100 rounded-[20px] p-6 flex gap-4">
          <div className="w-10 h-10 bg-red-100 text-red-500 rounded-xl flex items-center justify-center shrink-0">
            <ExclamationTriangleIcon className="w-6 h-6" />
          </div>
          <div>
            <h4 className="text-[15px] font-extrabold text-red-800 mb-1">LLM API Credits Low</h4>
            <p className="text-[13px] text-red-700/80 font-medium mb-2 leading-relaxed">
              Current balance: $12.45. Estimated exhaustion in 4 hours based on current load.
            </p>
            <a href="#" className="text-[12px] font-extrabold text-red-800 underline decoration-red-300 hover:decoration-red-800 underline-offset-4 transition-all">Top up now</a>
          </div>
        </div>

        {/* Alert 2 */}
        <div className="bg-slate-50 border border-slate-200 rounded-[20px] p-6 flex gap-4">
          <div className="w-10 h-10 bg-indigo-100/50 text-indigo-500 rounded-xl flex items-center justify-center shrink-0">
            <InformationCircleIcon className="w-6 h-6" />
          </div>
          <div>
            <h4 className="text-[15px] font-extrabold text-slate-800 mb-1">Database Indexing In Progress</h4>
            <p className="text-[13px] text-slate-500 font-medium leading-relaxed">
              Scheduled maintenance task running on 'users_v2' shard. Latency may increase by 5%.
            </p>
          </div>
        </div>

      </div>

      {/* Main Grid Layout */}
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-8 mt-2">
        
        {/* Left Column (Stats & Graphs) */}
        <div className="xl:col-span-8 flex flex-col gap-6">
          
          {/* API Connectivity Health */}
          <div className="bg-white border border-slate-200 rounded-[24px] p-8 shadow-sm">
            <div className="flex items-center justify-between mb-8">
              <h3 className="text-[16px] font-extrabold text-slate-900 tracking-tight">API Connectivity Health</h3>
              <span className="text-[12px] font-medium text-slate-500">Last 24 Hours</span>
            </div>

            <div className="flex flex-col gap-8">
              {/* Serper API */}
              <div className="flex items-center gap-6">
                <div className="w-14 h-14 bg-blue-50 text-brand-500 rounded-xl flex items-center justify-center shrink-0">
                  <MagnifyingGlassIcon className="w-6 h-6" strokeWidth={2} />
                </div>
                <div className="flex-1">
                  <div className="flex justify-between items-end mb-2">
                    <h4 className="text-[15px] font-bold text-slate-900">Serper API (Search)</h4>
                    <div className="text-right">
                      <div className="flex items-center gap-3">
                        <span className="px-2 py-0.5 bg-brand-50 text-brand-600 text-[10px] font-extrabold tracking-widest uppercase rounded">99.98% Uptime</span>
                        <span className="text-[15px] font-extrabold text-slate-900">24ms</span>
                      </div>
                      <span className="text-[10px] text-slate-400 font-medium">Avg Latency</span>
                    </div>
                  </div>
                  {/* Segmented Bar */}
                  <div className="flex gap-1 h-2.5 w-full">
                    <div className="flex-1 bg-brand-500 rounded-l-full"></div>
                    <div className="flex-1 bg-brand-500"></div>
                    <div className="flex-1 bg-brand-500"></div>
                    <div className="flex-1 bg-brand-500"></div>
                    <div className="flex-1 bg-brand-500"></div>
                    <div className="flex-1 bg-red-500"></div>
                    <div className="flex-1 bg-brand-500"></div>
                    <div className="flex-1 bg-brand-500 rounded-r-full"></div>
                  </div>
                </div>
              </div>

              {/* LLM Engine */}
              <div className="flex items-center gap-6">
                <div className="w-14 h-14 bg-slate-100 text-slate-500 rounded-xl flex items-center justify-center shrink-0">
                  <CpuChipIcon className="w-6 h-6" strokeWidth={2} />
                </div>
                <div className="flex-1">
                  <div className="flex justify-between items-end mb-2">
                    <h4 className="text-[15px] font-bold text-slate-900">LLM Core Engine (GPT-4o)</h4>
                    <div className="text-right">
                      <div className="flex items-center gap-3">
                        <span className="px-2 py-0.5 bg-purple-50 text-purple-600 text-[10px] font-extrabold tracking-widest uppercase rounded">98.4% Uptime</span>
                        <span className="text-[15px] font-extrabold text-slate-900">1.2s</span>
                      </div>
                      <span className="text-[10px] text-slate-400 font-medium">Avg Latency</span>
                    </div>
                  </div>
                  {/* Segmented Bar */}
                  <div className="flex gap-1 h-2.5 w-full">
                    <div className="flex-1 bg-slate-600 rounded-l-full"></div>
                    <div className="flex-1 bg-slate-600"></div>
                    <div className="flex-1 bg-slate-600"></div>
                    <div className="flex-1 bg-red-500"></div>
                    <div className="flex-1 bg-slate-600"></div>
                    <div className="flex-1 bg-slate-600"></div>
                    <div className="flex-1 bg-slate-600"></div>
                    <div className="flex-1 bg-slate-600 rounded-r-full"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Load & Latency Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            
            {/* Server Load */}
            <div className="bg-white border border-slate-200 rounded-[24px] p-8 shadow-sm relative overflow-hidden flex flex-col justify-between h-[220px]">
              <ServerStackIcon className="absolute right-6 top-6 w-12 h-12 text-slate-50" strokeWidth={1.5} />
              <div className="relative z-10">
                <p className="text-[11px] font-extrabold text-slate-500 uppercase tracking-widest mb-2">Server Load</p>
                <div className="flex items-baseline gap-3">
                  <h2 className="text-[44px] font-extrabold text-slate-900 tracking-tight leading-none">42%</h2>
                  <span className="text-[12px] font-extrabold text-brand-600">+5% increase</span>
                </div>
              </div>
              {/* Mini Bar Chart */}
              <div className="relative z-10 flex items-end gap-2 h-12 mt-4">
                <div className="w-full bg-brand-100 h-[40%] rounded-t-sm"></div>
                <div className="w-full bg-brand-100 h-[60%] rounded-t-sm"></div>
                <div className="w-full bg-brand-100 h-[30%] rounded-t-sm"></div>
                <div className="w-full bg-brand-100 h-[50%] rounded-t-sm"></div>
                <div className="w-full bg-brand-500 h-[100%] rounded-t-sm"></div>
                <div className="w-full bg-brand-100 h-[70%] rounded-t-sm"></div>
              </div>
            </div>

            {/* Database Latency */}
            <div className="bg-white border border-slate-200 rounded-[24px] p-8 shadow-sm relative overflow-hidden flex flex-col justify-between h-[220px]">
              <div className="absolute right-6 top-6 w-12 h-12 rounded-full border-4 border-slate-50 flex items-center justify-center">
                <div className="w-6 h-1 bg-slate-50 rounded-full"></div>
                <div className="w-6 h-1 bg-slate-50 rounded-full absolute mt-4"></div>
              </div>
              <div className="relative z-10">
                <p className="text-[11px] font-extrabold text-slate-500 uppercase tracking-widest mb-2">Database Latency</p>
                <div className="flex items-baseline gap-3">
                  <h2 className="text-[44px] font-extrabold text-slate-900 tracking-tight leading-none">12ms</h2>
                  <span className="text-[12px] font-extrabold text-emerald-500">-2ms decrease</span>
                </div>
              </div>
              {/* Mini Bar Chart */}
              <div className="relative z-10 flex items-end gap-2 h-12 mt-4">
                <div className="w-full bg-slate-200 h-[30%] rounded-t-sm"></div>
                <div className="w-full bg-slate-200 h-[40%] rounded-t-sm"></div>
                <div className="w-full bg-slate-200 h-[35%] rounded-t-sm"></div>
                <div className="w-full bg-slate-200 h-[20%] rounded-t-sm"></div>
                <div className="w-full bg-slate-200 h-[30%] rounded-t-sm"></div>
                <div className="w-full bg-slate-600 h-[50%] rounded-t-sm"></div>
              </div>
            </div>

          </div>

          {/* Error Distribution */}
          <div className="bg-white border border-slate-200 rounded-[24px] p-8 shadow-sm">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-[16px] font-extrabold text-slate-900 tracking-tight">Error Distribution</h3>
                <p className="text-[12px] text-slate-500 font-medium">Aggregated global request failures</p>
              </div>
              <button className="flex items-center gap-2 bg-slate-50 border border-slate-200 px-4 py-2 rounded-xl text-[12px] font-bold text-slate-600 hover:bg-slate-100 transition-colors">
                Last Hour <ChevronDownIcon className="w-4 h-4" />
              </button>
            </div>

            {/* Empty State Chart Area */}
            <div className="border border-dashed border-slate-200 bg-slate-50/50 rounded-2xl h-[140px] flex flex-col items-center justify-center mb-8">
              <ChartBarIcon className="w-8 h-8 text-slate-300 mb-2" />
              <p className="text-[12px] font-mono text-slate-400">0.02% global error rate - Healthy</p>
            </div>

            {/* Bottom Stats */}
            <div className="flex items-center justify-between px-8">
              <div className="text-center">
                <p className="text-[10px] font-extrabold text-slate-500 uppercase tracking-widest mb-1.5">4XX Client</p>
                <h4 className="text-[20px] font-extrabold text-slate-900">142</h4>
              </div>
              <div className="w-px h-10 bg-slate-200"></div>
              <div className="text-center">
                <p className="text-[10px] font-extrabold text-slate-500 uppercase tracking-widest mb-1.5">5XX Server</p>
                <h4 className="text-[20px] font-extrabold text-red-600">3</h4>
              </div>
              <div className="w-px h-10 bg-slate-200"></div>
              <div className="text-center">
                <p className="text-[10px] font-extrabold text-slate-500 uppercase tracking-widest mb-1.5">Timeouts</p>
                <h4 className="text-[20px] font-extrabold text-slate-900">12</h4>
              </div>
            </div>
          </div>

        </div>

        {/* Right Column (System Events Timeline) */}
        <div className="xl:col-span-4 bg-slate-50/80 border border-slate-200 rounded-[24px] p-8 shadow-sm flex flex-col">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-3">
              <ServerStackIcon className="w-5 h-5 text-slate-600" />
              <h3 className="text-[16px] font-extrabold text-slate-900 tracking-tight">System Events</h3>
            </div>
            <button className="text-slate-400 hover:text-slate-900 transition-colors">
              <AdjustmentsHorizontalIcon className="w-5 h-5" />
            </button>
          </div>

          <div className="flex-1 relative">
            {/* Vertical Line */}
            <div className="absolute left-[15px] top-4 bottom-4 w-px bg-slate-200"></div>

            <div className="flex flex-col gap-8 relative z-10">
              
              {/* Event 1 */}
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-full bg-white border border-slate-200 flex items-center justify-center shrink-0 shadow-sm mt-0.5">
                  <CheckCircleIcon className="w-5 h-5 text-emerald-500" />
                </div>
                <div>
                  <h4 className="text-[14px] font-bold text-slate-900 mb-1">Search Successful</h4>
                  <p className="text-[12px] text-slate-500 font-medium leading-snug mb-1">Query: "AI trend analysis 2024"<br/>Duration: 420ms</p>
                  <span className="text-[10px] font-mono text-slate-400">14:22:10 UTC</span>
                </div>
              </div>

              {/* Event 2 */}
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-full bg-white border border-slate-200 flex items-center justify-center shrink-0 shadow-sm mt-0.5">
                  <ArrowPathIcon className="w-4 h-4 text-brand-600" strokeWidth={2} />
                </div>
                <div>
                  <h4 className="text-[14px] font-bold text-slate-900 mb-1">Background Task: Cache Flush</h4>
                  <p className="text-[12px] text-slate-500 font-medium leading-snug mb-1">Cleared expired session tokens from Redis shard 01.</p>
                  <span className="text-[10px] font-mono text-slate-400">14:18:05 UTC</span>
                </div>
              </div>

              {/* Event 3 (Alert) */}
              <div className="flex items-start gap-4 bg-red-50/80 p-4 -mx-4 rounded-xl border border-red-100/50">
                <div className="w-8 h-8 rounded-full bg-white border border-red-200 flex items-center justify-center shrink-0 shadow-sm mt-0.5">
                  <ExclamationTriangleIcon className="w-4 h-4 text-red-500" strokeWidth={2} />
                </div>
                <div>
                  <h4 className="text-[14px] font-bold text-red-900 mb-1">API Timeout</h4>
                  <p className="text-[12px] text-red-700/80 font-medium leading-snug mb-1">LLM Provider (GPT-4) timed out after 30s retry.</p>
                  <span className="text-[10px] font-mono text-red-400">14:05:33 UTC</span>
                </div>
              </div>

              {/* Event 4 */}
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-full bg-white border border-slate-200 flex items-center justify-center shrink-0 shadow-sm mt-0.5">
                  <CheckCircleIcon className="w-5 h-5 text-emerald-500" />
                </div>
                <div>
                  <h4 className="text-[14px] font-bold text-slate-900 mb-1">User Login (OAuth)</h4>
                  <p className="text-[12px] text-slate-500 font-medium leading-snug mb-1">Admin user logged in from IP 192.168.1.44</p>
                  <span className="text-[10px] font-mono text-slate-400">13:58:12 UTC</span>
                </div>
              </div>

              {/* Event 5 */}
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-full bg-white border border-slate-200 flex items-center justify-center shrink-0 shadow-sm mt-0.5">
                  <CheckCircleIcon className="w-5 h-5 text-emerald-500" />
                </div>
                <div>
                  <h4 className="text-[14px] font-bold text-slate-900 mb-1">Daily Report Generated</h4>
                  <p className="text-[12px] text-slate-500 font-medium leading-snug mb-1">System performance report sent to 3 recipients.</p>
                  <span className="text-[10px] font-mono text-slate-400">13:00:00 UTC</span>
                </div>
              </div>

            </div>
          </div>

          <button className="w-full bg-slate-200/50 hover:bg-slate-200 text-slate-600 py-3.5 rounded-xl text-[12px] font-extrabold uppercase tracking-widest transition-colors mt-8">
            View All History
          </button>
        </div>

      </div>
    </div>
  );
}